import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { campaignsApi, type PerformanceRangeParams } from '../api/campaignsApi';
import { performanceKeys } from '../queryKeys';

/** Stable string key for a range selection, used in React Query keys. */
function rangeKey(range?: PerformanceRangeParams): string {
  if (!range?.range) return '30d';
  if (range.range === 'custom') return `custom:${range.from ?? ''}:${range.to ?? ''}`;
  return range.range;
}

/** A custom range is only "ready" to fetch once both endpoints are picked. */
function rangeReady(range?: PerformanceRangeParams): boolean {
  if (range?.range === 'custom') return !!(range.from && range.to);
  return true;
}

export function usePerformanceSummary(range?: PerformanceRangeParams) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: performanceKeys.summary(locationId, rangeKey(range)),
    queryFn: () => campaignsApi.performance.summary(locationId, range),
    enabled: !!locationId && rangeReady(range),
  });
}

export function useChannelBreakdown(range?: PerformanceRangeParams) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: performanceKeys.channels(locationId, rangeKey(range)),
    queryFn: () => campaignsApi.performance.channels(locationId, range),
    enabled: !!locationId && rangeReady(range),
  });
}

export function useCampaignBreakdown(range?: PerformanceRangeParams) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: performanceKeys.campaigns(locationId, rangeKey(range)),
    queryFn: () => campaignsApi.performance.campaigns(locationId, range),
    enabled: !!locationId && rangeReady(range),
  });
}

export function useTopSearchTerms(range?: PerformanceRangeParams) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: performanceKeys.searchTerms(locationId, rangeKey(range)),
    queryFn: () => campaignsApi.performance.searchTerms(locationId, range),
    enabled: !!locationId && rangeReady(range),
  });
}

export function useHeatmap(range?: PerformanceRangeParams) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: performanceKeys.heatmap(locationId, rangeKey(range)),
    queryFn: () => campaignsApi.performance.heatmap(locationId, range),
    enabled: !!locationId && rangeReady(range),
  });
}
